/* eslint-disable max-len */
import { PseoEntryBase } from './pseo/types';

export interface TemplateSection {
  heading: string;
  /** Form fields or checklist items, rendered as a list (or table columns when `table` is set). */
  fields?: string[];
  /** Paragraphs of ready-to-use copy. Blank lines separate paragraphs. */
  body?: string;
  /** Render `fields` as the columns of an empty table (e.g. a comment log). */
  table?: boolean;
  /** Render `fields` as checkboxes. */
  checklist?: boolean;
}

export interface ReviewTemplate extends Omit<PseoEntryBase, 'sourceUrls'> {
  title: string;
  /** One-sentence answer: what the template is for. */
  useCase: string;
  /** Plain-text intro under the H1. */
  intro: string;
  sections: TemplateSection[];
  downloadFormats: ('docx' | 'csv')[];
  /** Columns (and optional starter rows) for the CSV download. */
  csv?: { headers: string[]; rows?: string[][] };
  kreatliWorkflowPath: string;
  kreatliWorkflowLabel: string;
}

/** Templates are original content: publishable once marked verified (no external source needed). */
export const isTemplatePublishable = (template: ReviewTemplate) => template.status === 'verified';

const WRITTEN = '2026-10-16';
const changelog = [{ date: WRITTEN, change: 'First published.' }];

export const REVIEW_TEMPLATES: ReviewTemplate[] = [
  {
    slug: 'video-feedback-form',
    primaryQuery: 'video feedback form template',
    title: 'Video Feedback Form Template',
    useCase:
      'A form for collecting timecoded, prioritised feedback on one version of a video, ending in a clear approval decision.',
    intro:
      'Use this video feedback form for any review round: one form per version, one row per note, each pinned to a timecode and tagged with a priority so the editor knows what to fix first.',
    sections: [
      {
        heading: 'Project details',
        fields: [
          'Project name',
          'Client / brand',
          'Video title',
          'Version (e.g. v3)',
          'Review round (1st cut, revision, final)',
          'Reviewer name and role',
          'Review date',
          'Feedback due by',
        ],
      },
      {
        heading: 'Timecoded comments',
        body: 'Add one row per change. Use the timecode shown in the player (HH:MM:SS:FF) so the editor can jump straight to the frame.',
        fields: ['Timecode', 'Comment (what to change and why)', 'Category', 'Priority', 'Status'],
        table: true,
      },
      {
        heading: 'Categories and priorities',
        fields: [
          'Category: Picture, Edit/pacing, Graphics & text, Audio & music, Captions, Legal/brand',
          'Priority: Must fix (blocks approval), Should fix (this round if possible), Nice to have (optional)',
          'Status: Open, In progress, Done, Won’t fix (with reason)',
        ],
      },
      {
        heading: 'Overall notes',
        body: 'Anything that is not tied to a single moment: overall pacing, music choice, tone, length, call to action.',
      },
      {
        heading: 'Decision',
        fields: [
          '☐ Approved as is',
          '☐ Approved once the must-fix notes are done (no further review needed)',
          '☐ Changes required: send a new version for review',
          'Approver name',
          'Date',
        ],
      },
    ],
    downloadFormats: ['docx', 'csv'],
    csv: {
      headers: ['Version', 'Timecode', 'Comment', 'Category', 'Priority', 'Status', 'Reviewer'],
      rows: [
        [
          'v1',
          '00:00:12:05',
          'Logo appears too early; hold until the title card ends',
          'Graphics & text',
          'Must fix',
          'Open',
          '',
        ],
      ],
    },
    kreatliWorkflowPath: '/platform/video-feedback',
    kreatliWorkflowLabel: 'Collect timecoded feedback in Kreatli',
    lastVerified: WRITTEN,
    status: 'verified',
    changelog,
    faqs: [
      {
        question: 'What should a video feedback form include?',
        answer:
          'Project and version details, one row per comment with a timecode, a category and a priority, a place for overall notes, and a clear approval decision with the approver’s name and date.',
      },
      {
        question: 'Why add a priority to each comment?',
        answer:
          'Priorities separate notes that block approval from preferences. Editors fix the must-fix notes first, and reviewers can approve a version once those are done.',
      },
      {
        question: 'Can I use this form for client reviews?',
        answer:
          'Yes. Send one form per version so feedback from different rounds never mixes. If several people review, collect all notes on one form and resolve conflicts before sending it to the editor.',
      },
    ],
  },
  {
    slug: 'client-approval-email',
    primaryQuery: 'client approval email template',
    title: 'Client Approval Email Templates for Video',
    useCase:
      'Three ready-to-send emails for asking a client to review and approve a video: first cut, revision and final sign-off.',
    intro:
      'Copy the email that matches the stage of your project. Each one says what to review, what kind of feedback you need, how to send it and the deadline, which is what gets approvals back on time.',
    sections: [
      {
        heading: 'Email 1: first cut',
        body: `Subject: [Project name] first cut ready for your review (feedback by [date])

Hi [Client name],

The first cut of [video title] is ready: [review link]

At this stage we are looking for feedback on the story, structure and pacing. Colour, sound mix, captions and final graphics are still in progress, so there is no need to comment on those yet.

Please leave your notes directly on the video so each one is tied to the exact moment. If several people on your side are reviewing, could you combine everyone’s notes into one set of feedback?

To stay on schedule we need your feedback by [date and time]. After that we will send the revised version on [date].

Thanks,
[Your name]`,
      },
      {
        heading: 'Email 2: revision',
        body: `Subject: [Project name] v[number] with your changes (feedback by [date])

Hi [Client name],

Version [number] of [video title] is ready: [review link]

We have made the changes from the last round, including:
- [Change 1]
- [Change 2]
- [Change 3]

[If any requested change was not made, explain why in one line.]

Please check that the changes work for you and flag anything that still needs adjusting. As a reminder, this round includes [number] rounds of revisions under our agreement; further changes after the next round may affect the timeline.

Feedback by [date and time] keeps us on track for delivery on [date].

Thanks,
[Your name]`,
      },
      {
        heading: 'Email 3: final sign-off',
        body: `Subject: [Project name] final version for approval

Hi [Client name],

The final version of [video title] is ready for sign-off: [review link]

This version includes the final colour grade, sound mix, captions and graphics, and is exported to the agreed specs: [format, resolution, frame rate, length].

If everything looks right, please reply “Approved” or click Approve on the review page by [date]. Once approved, we will deliver the final files to [location / platform] on [date].

If you spot anything that must change before release, please list it in this round so we can include it in the delivery.

Thanks,
[Your name]`,
      },
    ],
    downloadFormats: ['docx'],
    kreatliWorkflowPath: '/platform/review-approval',
    kreatliWorkflowLabel: 'Send review links and track approvals in Kreatli',
    lastVerified: WRITTEN,
    status: 'verified',
    changelog,
    faqs: [
      {
        question: 'How do I ask a client to approve a video?',
        answer:
          'Send a short email with a review link, say what stage the video is at and what feedback you need, ask for one consolidated set of notes, and give a deadline. For the final version, ask for an explicit “Approved”.',
      },
      {
        question: 'How do I stop clients from giving feedback on the wrong things?',
        answer:
          'Tell them what is still in progress. On a first cut, say that colour, sound and captions are not final, so they focus on story and pacing.',
      },
      {
        question: 'What counts as approval?',
        answer:
          'A written “Approved” reply or an approval recorded on the review page for a specific version. Keep it with the project so there is no doubt later about which version was signed off.',
      },
    ],
  },
  {
    slug: 'revision-request-email',
    primaryQuery: 'revision request email template',
    title: 'Revision Request Email Template',
    useCase: 'An email for sending clear, consolidated change requests to an editor or agency.',
    intro:
      'Use this template when you are the reviewer: it turns scattered notes into one clear request that an editor can act on without follow-up questions.',
    sections: [
      {
        heading: 'Email template',
        body: `Subject: [Project name] v[number]: revision requests (round [number])

Hi [Editor name],

Thanks for version [number] of [video title]. Here is our consolidated feedback from [names of reviewers].

Must fix before approval:
1. [Timecode] – [what to change] – [why]
2. [Timecode] – [what to change] – [why]

Should fix if possible:
3. [Timecode] – [what to change]

Nice to have (your call):
4. [Timecode] – [suggestion]

Overall: [one or two sentences on pacing, tone or length, if needed].

We have attached / linked [new assets, logos, legal copy] where needed. Could you send the next version by [date]? If any of these changes affect the timeline or budget, let us know before starting.

Thanks,
[Your name]`,
      },
      {
        heading: 'Checklist before you send',
        fields: [
          'Every note has a timecode',
          'Each note says what to change, not only what is wrong',
          'Notes from all reviewers are combined and conflicts resolved',
          'Notes are sorted by priority',
          'New assets (logos, copy, music) are attached or linked',
          'The deadline for the next version is stated',
        ],
        checklist: true,
      },
    ],
    downloadFormats: ['docx'],
    kreatliWorkflowPath: '/platform/video-feedback',
    kreatliWorkflowLabel: 'Pin revision requests to frames in Kreatli',
    lastVerified: WRITTEN,
    status: 'verified',
    changelog,
    faqs: [
      {
        question: 'How do I write a revision request for a video?',
        answer:
          'List each change with its timecode, say what to change and why, sort the list by priority, combine notes from every reviewer and give a deadline for the next version.',
      },
      {
        question: 'Should I send notes from each reviewer separately?',
        answer:
          'No. Combine them and resolve conflicts first. Contradictory notes from different people are the most common cause of extra revision rounds.',
      },
    ],
  },
  {
    slug: 'creative-brief',
    primaryQuery: 'video production creative brief template',
    title: 'Creative Brief Template for Video Production',
    useCase: 'A one-page brief that aligns client, producer and editor on a video before production starts.',
    intro:
      'Fill this brief in before kickoff. It covers what the video must achieve, who it is for, what has to be delivered and who approves it, so review rounds are measured against the same goals.',
    sections: [
      {
        heading: 'Project overview',
        fields: [
          'Project name',
          'Client / brand',
          'Project owner (client side)',
          'Producer / agency contact',
          'Brief date',
        ],
      },
      {
        heading: 'Objective',
        body: 'What should this video achieve? One measurable goal is better than three vague ones (e.g. “drive sign-ups for the spring launch from paid social”).',
      },
      {
        heading: 'Audience',
        fields: [
          'Who is watching?',
          'What do they know or believe today?',
          'What should they think, feel or do after watching?',
        ],
      },
      {
        heading: 'Key message',
        body: 'The single thing viewers must remember, in one sentence. Add up to three supporting points.',
      },
      {
        heading: 'Deliverables',
        fields: [
          'Master video: length, resolution, frame rate',
          'Cut-downs: lengths (e.g. 30s, 15s, 6s)',
          'Aspect ratios per platform (16:9, 9:16, 1:1, 4:5)',
          'Captions / subtitles: languages, burned in or sidecar files',
          'Stills, thumbnails or GIFs',
          'File formats and delivery location',
        ],
      },
      {
        heading: 'Tone and references',
        fields: ['Three words for the tone', 'Reference videos (links) and what to take from each', 'What to avoid'],
      },
      {
        heading: 'Mandatories',
        fields: [
          'Logo and end card rules',
          'Legal lines and disclaimers',
          'Brand fonts and colours',
          'Music licensing requirements',
          'Accessibility requirements',
        ],
      },
      {
        heading: 'Budget and timeline',
        fields: ['Budget range', 'Shoot date(s)', 'First cut date', 'Final delivery date', 'Launch / publish date'],
      },
      {
        heading: 'Review and approval',
        fields: [
          'Who gives feedback',
          'Who has final approval',
          'Number of revision rounds included',
          'Feedback turnaround time per round',
          'Where feedback is collected (one review link per version)',
        ],
      },
      {
        heading: 'Distribution',
        fields: ['Channels and placements', 'Paid or organic', 'Safe zones to respect (e.g. Reels, TikTok, Stories)'],
      },
    ],
    downloadFormats: ['docx'],
    kreatliWorkflowPath: '/solutions/use-case/creative-production-management',
    kreatliWorkflowLabel: 'Run the production in Kreatli',
    lastVerified: WRITTEN,
    status: 'verified',
    changelog,
    faqs: [
      {
        question: 'What goes in a video production creative brief?',
        answer:
          'The objective, audience, key message, deliverables with formats and aspect ratios, tone and references, mandatories, budget and timeline, who reviews and approves, and where the video will run.',
      },
      {
        question: 'Why include review and approval in a brief?',
        answer:
          'Agreeing up front who approves, how many rounds are included and how fast feedback comes back prevents the most common delays in post-production.',
      },
      {
        question: 'How long should a creative brief be?',
        answer: 'One to two pages. If a section needs more, link to a separate document rather than growing the brief.',
      },
    ],
  },
  {
    slug: 'video-review-checklist',
    primaryQuery: 'video review checklist',
    title: 'Video Review Checklist: QC Before Approval',
    useCase:
      'A quality-control checklist for picture, audio, captions, safe zones and export specs before a video is approved.',
    intro:
      'Run through this checklist on every version before you send it for approval or delivery. It catches the technical issues clients notice last and platforms reject first.',
    sections: [
      {
        heading: 'Picture',
        fields: [
          'No flash frames, black frames or jump cuts between shots',
          'Colour is consistent from shot to shot',
          'No dead pixels, compression blocks or banding in gradients',
          'Graphics and lower thirds are spelled correctly and on brand',
          'Logo and end card match the brand guidelines',
        ],
        checklist: true,
      },
      {
        heading: 'Audio',
        fields: [
          'Dialogue is clear over music',
          'Loudness meets the delivery spec (e.g. platform or broadcast target)',
          'No clicks, pops, clipping or dropouts',
          'Music is licensed for every channel the video will run on',
          'Audio is in sync with picture from start to end',
        ],
        checklist: true,
      },
      {
        heading: 'Captions',
        fields: [
          'Captions match the final audio word for word',
          'Timing is in sync and lines are readable at normal speed',
          'Speaker names and sound cues are included where needed',
          'Captions sit inside the safe area and do not cover key visuals',
        ],
        checklist: true,
      },
      {
        heading: 'Safe zones',
        fields: [
          'Text, logos and CTAs are inside the platform safe zone for each placement',
          'Vertical cut-downs are checked against Reels, TikTok, Shorts and Stories overlays',
          'Faces and products are not covered by buttons or captions',
        ],
        checklist: true,
      },
      {
        heading: 'Export specs',
        fields: [
          'Resolution and aspect ratio match the brief for each deliverable',
          'Frame rate matches the source (no accidental conversion)',
          'Codec, container and bitrate match the delivery spec',
          'Duration matches the agreed length',
          'File names follow the agreed naming convention and version number',
        ],
        checklist: true,
      },
    ],
    downloadFormats: ['docx', 'csv'],
    csv: {
      headers: ['Section', 'Check', 'Pass / Fail', 'Notes'],
    },
    kreatliWorkflowPath: '/platform/review-approval',
    kreatliWorkflowLabel: 'Review and approve versions in Kreatli',
    lastVerified: WRITTEN,
    status: 'verified',
    changelog,
    faqs: [
      {
        question: 'What should I check before approving a video?',
        answer:
          'Picture (flash frames, colour, graphics), audio (clarity, loudness, sync, licensing), captions (accuracy and timing), safe zones for each platform, and export specs (resolution, frame rate, codec, duration, file names).',
      },
      {
        question: 'When should I run a video QC checklist?',
        answer: 'On every version you send for approval, and once more on the final exports before delivery.',
      },
      {
        question: 'How do I check safe zones for social video?',
        answer:
          'Preview the cut with each platform’s interface overlay, for example with a free safe zone checker, and move any text or logos that the buttons or captions cover.',
      },
    ],
  },
];

export const TEMPLATES_HUB_PATH = '/templates';

export const templatePath = (template: ReviewTemplate) => `${TEMPLATES_HUB_PATH}/${template.slug}`;

export const getPublishedTemplates = () => REVIEW_TEMPLATES.filter(isTemplatePublishable);

export const getTemplate = (slug: string) => getPublishedTemplates().find((template) => template.slug === slug);

/** CSV rows for the download: explicit rows, or one row per checklist item for checklist templates. */
export function templateCsvRows(template: ReviewTemplate): string[][] {
  if (!template.csv) return [];
  if (template.csv.rows) return template.csv.rows;
  return template.sections.flatMap((section) =>
    section.checklist ? (section.fields ?? []).map((field) => [section.heading, field, '', '']) : [],
  );
}

/** Plain-text version of the template (free "Copy to clipboard"). */
export function templateToPlainText(template: ReviewTemplate): string {
  const parts = [template.title.toUpperCase(), ''];
  for (const section of template.sections) {
    parts.push(section.heading.toUpperCase());
    if (section.body) parts.push(section.body);
    if (section.fields) {
      if (section.table) parts.push(section.fields.join(' | '));
      else parts.push(...section.fields.map((field) => (section.checklist ? `[ ] ${field}` : `${field}: ________`)));
    }
    parts.push('');
  }
  return parts.join('\n').trim();
}
